// QuickCommandsDialog.js
// Dialog for quickly adding multiple transactions using shorthand commands
// Keyboard-driven UX: Ctrl+K or / to search, Enter to navigate fields, Ctrl+Enter to submit

import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Chip,
  Autocomplete,
  Paper,
  Divider,
  Tooltip,
  Popover,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  InputAdornment,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { he } from 'date-fns/locale';
import { useQuery } from '@tanstack/react-query';
import MobileDialog from './MobileDialog';
import API from '../../services/api';
import { getGeneralTransactionTags, getCashDesks } from '../../services/cashflowService';
import useQuickCommands from '../../useQuickCommands';
import { useUser } from '../../contexts/UserContext';
import {
  COMMAND_TYPES,
  COMMAND_STATUS,
  FREQUENCY_OPTIONS,
  PERIOD_TYPE_OPTIONS,
  getEnabledCommandTypes,
} from '../../quickCommandsConfig';

// Icons
import CloseIcon from '@mui/icons-material/Close';
import PaymentIcon from '@mui/icons-material/Payment';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RepeatIcon from '@mui/icons-material/Repeat';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import RefreshIcon from '@mui/icons-material/Refresh';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';

// Icon mapping for command types
const COMMAND_ICONS = {
  SP: PaymentIcon,
  SPU: ShoppingCartIcon,
  GE: RemoveCircleIcon,
  GI: AddCircleIcon,
  RE: RepeatIcon,
  RI: RepeatIcon,
  CC: PersonAddIcon,
  CP: PersonRemoveIcon,
  CD: PointOfSaleIcon,
};

// Status icon component
const StatusIcon = ({ status, error }) => {
  switch (status) {
    case COMMAND_STATUS.SUCCESS:
      return (
        <Tooltip title="הצליח">
          <CheckCircleIcon color="success" />
        </Tooltip>
      );
    case COMMAND_STATUS.ERROR:
      return (
        <Tooltip title={error || 'שגיאה'}>
          <ErrorIcon color="error" />
        </Tooltip>
      );
    case COMMAND_STATUS.SUBMITTING:
      return <CircularProgress size={20} />;
    default:
      return (
        <Tooltip title="ממתין">
          <HourglassEmptyIcon color="disabled" />
        </Tooltip>
      );
  }
};

// Command Search Popup Component
const CommandSearchPopup = ({
  open,
  anchorEl,
  onClose,
  commandTypes,
  onSelectCommand,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const searchInputRef = useRef(null);

  // Filter commands based on search term
  const filteredCommands = useMemo(() => {
    if (!searchTerm) return commandTypes;
    const term = searchTerm.toLowerCase();
    return commandTypes.filter((code) => {
      const config = COMMAND_TYPES[code];
      if (!config) return false;
      return (
        code.toLowerCase().includes(term) ||
        config.label.toLowerCase().includes(term) ||
        config.labelEn.toLowerCase().includes(term)
      );
    });
  }, [commandTypes, searchTerm]);

  // Reset state when popup opens
  useEffect(() => {
    if (open) {
      setSearchTerm('');
      setSelectedIndex(0);
      // Focus search input after a brief delay
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
    }
  }, [open]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < filteredCommands.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredCommands.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredCommands[selectedIndex]) {
          onSelectCommand(filteredCommands[selectedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        onClose();
        break;
      default:
        break;
    }
  };

  // Reset selected index when filtered list changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [filteredCommands.length]);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      PaperProps={{
        sx: {
          width: 350,
          display: 'flex',
          flexDirection: 'column',
          maxHeight: 400,
        },
      }}
    >
      <Box sx={{ p: 1, flexShrink: 0 }}>
        <TextField
          inputRef={searchInputRef}
          fullWidth
          size="small"
          placeholder="חפש פקודה... (הקלד קוד או שם)"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={handleKeyDown}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
          autoFocus
        />
      </Box>
      <Divider sx={{ flexShrink: 0 }} />
      <List dense sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        {filteredCommands.length === 0 ? (
          <ListItem>
            <ListItemText
              primary="לא נמצאו פקודות"
              sx={{ textAlign: 'center', color: 'text.secondary' }}
            />
          </ListItem>
        ) : (
          filteredCommands.map((code, index) => {
            const config = COMMAND_TYPES[code];
            if (!config) return null;
            const IconComponent = COMMAND_ICONS[code] || AddCircleIcon;
            const isSelected = index === selectedIndex;

            return (
              <ListItemButton
                key={code}
                selected={isSelected}
                onClick={() => onSelectCommand(code)}
                sx={{
                  backgroundColor: isSelected ? 'action.selected' : 'transparent',
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <IconComponent color={config.color} />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={code}
                        size="small"
                        color={config.color}
                        sx={{ fontWeight: 'bold', minWidth: 50 }}
                      />
                      <Typography variant="body2">{config.label}</Typography>
                    </Box>
                  }
                />
              </ListItemButton>
            );
          })
        )}
      </List>
      <Divider sx={{ flexShrink: 0 }} />
      <Box sx={{ p: 1, display: 'flex', gap: 1, justifyContent: 'center', flexShrink: 0 }}>
        <Chip size="small" label="↑↓ לנווט" variant="outlined" />
        <Chip size="small" label="Enter לבחור" variant="outlined" />
        <Chip size="small" label="Esc לסגור" variant="outlined" />
      </Box>
    </Popover>
  );
};

// Command Row Component with ref forwarding for focus management
const CommandRow = React.forwardRef(({
  command,
  suppliers,
  clients,
  cashDesks,
  incomeTags,
  expenseTags,
  validDebtFields = [],
  debtFieldsSchema = {},
  onUpdate,
  onRemove,
  disabled,
  isHighlighted,
  onFieldKeyDown,
  fieldRefs,
}, ref) => {
  const config = COMMAND_TYPES[command.type];
  if (!config) return null;

  const isDisabled = disabled || command.status === COMMAND_STATUS.SUCCESS;
  const hasError = command.status === COMMAND_STATUS.ERROR;

  // Check if there's only one debt field (auto-selected, no need to show dropdown)
  const hasSingleDebtField = validDebtFields.length === 1;

  // Get visible fields (respecting showWhen conditions and hiding debt_field when single option)
  const visibleFields = config.fields.filter((field) => {
    // Hide debt_field dropdown when there's only one option (already auto-selected)
    if (field.type === 'debt_field' && hasSingleDebtField) return false;
    if (!field.showWhen) return true;
    const conditionValue = command.data[field.showWhen.field];
    return conditionValue === field.showWhen.value;
  });

  // Handle Enter key on fields to navigate to next
  const handleFieldKeyDown = (e, fieldIndex) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onFieldKeyDown(command.id, fieldIndex);
    }
  };

  // Render field based on type
  const renderField = (field, fieldIndex) => {
    const value = command.data[field.name];
    const fieldRefKey = `${command.id}-${field.name}`;

    // Check conditional visibility
    if (field.showWhen) {
      const conditionValue = command.data[field.showWhen.field];
      if (conditionValue !== field.showWhen.value) {
        return null;
      }
    }

    switch (field.type) {
      case 'supplier':
        return (
          <Autocomplete
            key={field.name}
            size="small"
            options={suppliers}
            getOptionLabel={(option) => option?.SupplierName || ''}
            value={suppliers.find((s) => s.SupplierID === value) || null}
            onChange={(e, newValue) =>
              onUpdate(command.id, field.name, newValue?.SupplierID || '')
            }
            disabled={isDisabled}
            renderInput={(params) => (
              <TextField
                {...params}
                inputRef={(el) => {
                  if (fieldRefs) fieldRefs.current[fieldRefKey] = el;
                }}
                label={field.label}
                required={field.required}
                error={hasError && field.required && !value}
                size="small"
                onKeyDown={(e) => handleFieldKeyDown(e, fieldIndex)}
              />
            )}
            sx={{ minWidth: 150, flex: 1 }}
          />
        );

      case 'client':
        return (
          <Autocomplete
            key={field.name}
            size="small"
            options={clients}
            getOptionLabel={(option) =>
              option?.ClientName
                ? `${option.ClientName} (${option.ClientBusinessID})`
                : ''
            }
            value={clients.find((c) => c.ClientBusinessID === value) || null}
            onChange={(e, newValue) =>
              onUpdate(command.id, field.name, newValue?.ClientBusinessID || '')
            }
            disabled={isDisabled}
            renderInput={(params) => (
              <TextField
                {...params}
                inputRef={(el) => {
                  if (fieldRefs) fieldRefs.current[fieldRefKey] = el;
                }}
                label={field.label}
                required={field.required}
                error={hasError && field.required && !value}
                size="small"
                onKeyDown={(e) => handleFieldKeyDown(e, fieldIndex)}
              />
            )}
            sx={{ minWidth: 180, flex: 1 }}
          />
        );

      case 'cashdesk':
        return (
          <Autocomplete
            key={field.name}
            size="small"
            options={cashDesks}
            getOptionLabel={(option) => option?.Name || ''}
            value={cashDesks.find((cd) => cd.CashDeskID === value) || null}
            onChange={(e, newValue) =>
              onUpdate(command.id, field.name, newValue?.CashDeskID || '')
            }
            disabled={isDisabled}
            renderInput={(params) => (
              <TextField
                {...params}
                inputRef={(el) => {
                  if (fieldRefs) fieldRefs.current[fieldRefKey] = el;
                }}
                label={field.label}
                required={field.required}
                error={hasError && field.required && !value}
                size="small"
                onKeyDown={(e) => handleFieldKeyDown(e, fieldIndex)}
              />
            )}
            sx={{ minWidth: 150, flex: 1 }}
          />
        );

      case 'debt_field':
        // Only shown when there are multiple debt fields (single field is auto-selected)
        return (
          <FormControl
            key={field.name}
            size="small"
            sx={{ minWidth: 120 }}
            disabled={isDisabled}
            error={hasError && field.required && !value}
          >
            <InputLabel required={field.required}>{field.label}</InputLabel>
            <Select
              value={value || ''}
              label={field.label}
              onChange={(e) => onUpdate(command.id, field.name, e.target.value)}
              inputRef={(el) => {
                if (fieldRefs) fieldRefs.current[fieldRefKey] = el;
              }}
              onKeyDown={(e) => handleFieldKeyDown(e, fieldIndex)}
            >
              {validDebtFields.map((df) => (
                <MenuItem key={df} value={df}>
                  {debtFieldsSchema[df]?.label || df}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 'tag':
        const tags = field.tagType === 'income' ? incomeTags : expenseTags;
        return (
          <FormControl
            key={field.name}
            size="small"
            sx={{ minWidth: 120 }}
            disabled={isDisabled}
          >
            <InputLabel>{field.label}</InputLabel>
            <Select
              inputRef={(el) => {
                if (fieldRefs) fieldRefs.current[fieldRefKey] = el;
              }}
              value={value || ''}
              label={field.label}
              onChange={(e) => onUpdate(command.id, field.name, e.target.value)}
              onKeyDown={(e) => handleFieldKeyDown(e, fieldIndex)}
            >
              <MenuItem value="">
                <em>ללא</em>
              </MenuItem>
              {tags.map((tag) => (
                <MenuItem key={tag.TagID} value={tag.TagID}>
                  {tag.Name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 'select':
        return (
          <FormControl
            key={field.name}
            size="small"
            sx={{ minWidth: 100 }}
            disabled={isDisabled}
            required={field.required}
          >
            <InputLabel>{field.label}</InputLabel>
            <Select
              inputRef={(el) => {
                if (fieldRefs) fieldRefs.current[fieldRefKey] = el;
              }}
              value={value || field.default || ''}
              label={field.label}
              onChange={(e) => onUpdate(command.id, field.name, e.target.value)}
              onKeyDown={(e) => handleFieldKeyDown(e, fieldIndex)}
            >
              {field.options.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {FREQUENCY_OPTIONS[opt] || PERIOD_TYPE_OPTIONS[opt] || opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 'date':
        return (
          <DatePicker
            key={field.name}
            label={field.label}
            value={value || null}
            onChange={(newValue) => onUpdate(command.id, field.name, newValue)}
            disabled={isDisabled}
            slotProps={{
              textField: {
                inputRef: (el) => {
                  if (fieldRefs) fieldRefs.current[fieldRefKey] = el;
                },
                size: 'small',
                required: field.required,
                error: hasError && field.required && !value,
                sx: { minWidth: 140 },
                onKeyDown: (e) => handleFieldKeyDown(e, fieldIndex),
              },
            }}
          />
        );

      case 'number':
        return (
          <TextField
            key={field.name}
            inputRef={(el) => {
              if (fieldRefs) fieldRefs.current[fieldRefKey] = el;
            }}
            label={field.label}
            type="number"
            size="small"
            value={value}
            onChange={(e) => onUpdate(command.id, field.name, e.target.value)}
            required={field.required}
            disabled={isDisabled}
            error={hasError && field.required && !value}
            inputProps={{
              min: field.min,
              max: field.max,
              step: field.name === 'amount' || field.name === 'total_amount' ? '0.01' : '1',
            }}
            sx={{ minWidth: field.name.includes('day') ? 80 : 100 }}
            onKeyDown={(e) => handleFieldKeyDown(e, fieldIndex)}
          />
        );

      case 'text':
      default:
        return (
          <TextField
            key={field.name}
            inputRef={(el) => {
              if (fieldRefs) fieldRefs.current[fieldRefKey] = el;
            }}
            label={field.label}
            size="small"
            value={value}
            onChange={(e) => onUpdate(command.id, field.name, e.target.value)}
            required={field.required}
            disabled={isDisabled}
            error={hasError && field.required && !value}
            sx={{ minWidth: 100, flex: field.name === 'note' ? 1 : 0 }}
            onKeyDown={(e) => handleFieldKeyDown(e, fieldIndex)}
          />
        );
    }
  };

  return (
    <Paper
      ref={ref}
      elevation={1}
      sx={{
        p: 1.5,
        mb: 1,
        opacity: command.status === COMMAND_STATUS.SUCCESS ? 0.6 : 1,
        backgroundColor:
          command.status === COMMAND_STATUS.ERROR
            ? 'error.lighter'
            : command.status === COMMAND_STATUS.SUCCESS
            ? 'success.lighter'
            : 'background.paper',
        border: hasError ? '1px solid' : 'none',
        borderColor: 'error.main',
        // Highlight animation
        animation: isHighlighted ? 'highlight 0.6s ease-out' : 'none',
        '@keyframes highlight': {
          '0%': {
            backgroundColor: 'primary.light',
            transform: 'scale(1.01)',
          },
          '100%': {
            backgroundColor: 'background.paper',
            transform: 'scale(1)',
          },
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        {/* Command ID badge */}
        <Chip
          label={command.id}
          size="small"
          color={config.color}
          sx={{ fontWeight: 'bold', minWidth: 60 }}
        />

        {/* Fields */}
        {visibleFields.map((field, idx) => renderField(field, idx))}

        {/* Status indicator */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
          <StatusIcon status={command.status} error={command.error} />

          {/* Remove button */}
          <IconButton
            size="small"
            onClick={() => onRemove(command.id)}
            disabled={command.status === COMMAND_STATUS.SUBMITTING}
            color="error"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Error message */}
      {command.error && (
        <Typography
          variant="caption"
          color="error"
          sx={{ display: 'block', mt: 0.5, mr: 1 }}
        >
          {command.error}
        </Typography>
      )}
    </Paper>
  );
});

// Main Dialog Component
const QuickCommandsDialog = ({ open, onClose }) => {
  // Get user profile for debt fields configuration
  const { userProfile } = useUser();
  const debtFields = userProfile?.ui_config?.debt_fields || [];
  const debtFieldsSchema = userProfile?.ui_config?.debt_fields_schema || {};
  // Get valid debt fields (excluding TotalDebt) for CC/CP commands
  const validDebtFields = useMemo(
    () => debtFields.filter((f) => f !== 'TotalDebt'),
    [debtFields]
  );

  const {
    commands,
    isSubmitting,
    summary,
    canSubmit,
    hasFailed,
    hasSuccessful,
    addCommand,
    updateCommand,
    removeCommand,
    submitAll,
    retryFailed,
    clearAll,
    clearSuccessful,
  } = useQuickCommands({ debtFields, debtFieldsSchema });

  // Command search popup state
  const [searchPopupOpen, setSearchPopupOpen] = useState(false);
  const [searchPopupAnchor, setSearchPopupAnchor] = useState(null);
  const searchButtonRef = useRef(null);

  // Highlighted command (for animation after adding)
  const [highlightedCommandId, setHighlightedCommandId] = useState(null);

  // Field refs for focus management
  const fieldRefs = useRef({});
  const commandRowRefs = useRef({});

  // Get all command types
  const commandTypes = useMemo(() => getEnabledCommandTypes(), []);

  // Fetch suppliers
  const { data: suppliers = [], isLoading: isLoadingSuppliers } = useQuery({
    queryKey: ['suppliers'],
    queryFn: async () => {
      const response = await API.get('/suppliers/');
      return response.data;
    },
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch income tags
  const { data: incomeTags = [] } = useQuery({
    queryKey: ['general-transaction-tags', 'income'],
    queryFn: () => getGeneralTransactionTags('income'),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch expense tags
  const { data: expenseTags = [] } = useQuery({
    queryKey: ['general-transaction-tags', 'expense'],
    queryFn: () => getGeneralTransactionTags('expense'),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch clients (for CC/CP commands)
  const { data: clients = [] } = useQuery({
    queryKey: ['clients', 'light'],
    queryFn: async () => {
      const response = await API.get('/clients/', { params: { light: true, limit: 500 } });
      return response.data;
    },
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch cash desks (for CD command)
  const { data: cashDesks = [] } = useQuery({
    queryKey: ['cash-desks'],
    queryFn: () => getCashDesks(true),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  // Open command search popup
  const openSearchPopup = useCallback(() => {
    setSearchPopupAnchor(searchButtonRef.current);
    setSearchPopupOpen(true);
  }, []);

  // Close command search popup
  const closeSearchPopup = useCallback(() => {
    setSearchPopupOpen(false);
  }, []);

  // Handle command selection from search popup
  const handleSelectCommand = useCallback((code) => {
    const newCommandId = addCommand(code);
    closeSearchPopup();

    if (newCommandId) {
      // Set highlight for animation
      setHighlightedCommandId(newCommandId);

      // Clear highlight after animation
      setTimeout(() => {
        setHighlightedCommandId(null);
      }, 700);

      // Focus first field of the new command after a brief delay
      setTimeout(() => {
        const config = COMMAND_TYPES[code];
        if (config && config.fields.length > 0) {
          const firstField = config.fields[0];
          const fieldRefKey = `${newCommandId}-${firstField.name}`;
          const fieldEl = fieldRefs.current[fieldRefKey];
          if (fieldEl) {
            fieldEl.focus();
          }
        }
      }, 100);
    }
  }, [addCommand, closeSearchPopup]);

  // Handle Enter key on fields to navigate to next required field
  const handleFieldKeyDown = useCallback((commandId, currentFieldIndex) => {
    const command = commands.find((c) => c.id === commandId);
    if (!command) return;

    const config = COMMAND_TYPES[command.type];
    if (!config) return;

    // Get visible fields
    const visibleFields = config.fields.filter((field) => {
      if (!field.showWhen) return true;
      const conditionValue = command.data[field.showWhen.field];
      return conditionValue === field.showWhen.value;
    });

    // Find next field (prioritize required fields)
    let nextFieldIndex = currentFieldIndex + 1;

    // Skip to next required field if current field is optional and empty
    while (nextFieldIndex < visibleFields.length) {
      const nextField = visibleFields[nextFieldIndex];
      const currentField = visibleFields[currentFieldIndex];

      // If current field is not required and empty, skip optional fields
      if (!currentField?.required && !command.data[currentField?.name]) {
        if (nextField.required || command.data[nextField.name]) {
          break;
        }
      } else {
        break;
      }
      nextFieldIndex++;
    }

    if (nextFieldIndex < visibleFields.length) {
      const nextField = visibleFields[nextFieldIndex];
      const fieldRefKey = `${commandId}-${nextField.name}`;
      const fieldEl = fieldRefs.current[fieldRefKey];
      if (fieldEl) {
        fieldEl.focus();
      }
    } else {
      // No more fields in this command, open search popup to add more
      openSearchPopup();
    }
  }, [commands, openSearchPopup]);

  // Global keyboard event handler
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e) => {
      // Ctrl+K or / to open search (when not in search popup)
      if (
        (e.key === 'k' && (e.ctrlKey || e.metaKey)) ||
        (e.key === '/' && !searchPopupOpen && e.target.tagName !== 'INPUT' && e.target.tagName !== 'TEXTAREA')
      ) {
        e.preventDefault();
        openSearchPopup();
        return;
      }

      // Ctrl+Enter to submit all
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && canSubmit) {
        e.preventDefault();
        submitAll();
        return;
      }

      // Escape to close search popup (handled by popup itself)
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, searchPopupOpen, openSearchPopup, canSubmit, submitAll]);

  const handleClose = () => {
    if (isSubmitting) return;
    clearAll();
    onClose();
  };

  const handleSubmit = async () => {
    await submitAll();
  };

  const handleRetry = async () => {
    await retryFailed();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={he}>
      <MobileDialog
        open={open}
        onClose={(event, reason) => {
          if (reason === 'backdropClick') return;
          handleClose();
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: '60vh',
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6">פקודות מהירות</Typography>
            <Chip
              size="small"
              icon={<KeyboardIcon />}
              label="Ctrl+K לחיפוש"
              variant="outlined"
              sx={{ fontSize: '0.7rem', direction: 'ltr' }}
            />
          </Box>
          <IconButton onClick={handleClose} disabled={isSubmitting}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {/* Search Button & Quick Action Buttons */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
              <Button
                ref={searchButtonRef}
                variant="outlined"
                color="primary"
                startIcon={<SearchIcon />}
                onClick={openSearchPopup}
                disabled={isSubmitting}
                sx={{ minWidth: 160, direction: 'ltr' }}
              >
                חפש פקודה (/)
              </Button>
              <Typography variant="body2" color="text.secondary">
                או בחר:
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {commandTypes.map((code) => {
                const config = COMMAND_TYPES[code];
                if (!config) return null;
                const IconComponent = COMMAND_ICONS[code] || AddCircleIcon;

                return (
                  <Button
                    key={code}
                    variant="contained"
                    color={config.color}
                    size="small"
                    startIcon={<IconComponent />}
                    onClick={() => handleSelectCommand(code)}
                    disabled={isSubmitting}
                    sx={{ minWidth: 100, direction: 'ltr' }}
                  >
                    {config.label}
                  </Button>
                );
              })}
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Commands List */}
          <Box sx={{ mb: 2 }}>
            {commands.length === 0 ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                לחץ Ctrl+K או "/" לחיפוש פקודה, או בחר מהכפתורים למעלה
              </Alert>
            ) : (
              <>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  פקודות ({commands.length}):
                  <Typography component="span" variant="caption" sx={{ ml: 1 }}>
                    (Enter לעבור לשדה הבא, Ctrl+Enter לשלוח)
                  </Typography>
                </Typography>

                {isLoadingSuppliers ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  commands.map((command) => (
                    <CommandRow
                      key={command.id}
                      ref={(el) => {
                        commandRowRefs.current[command.id] = el;
                      }}
                      command={command}
                      suppliers={suppliers}
                      clients={clients}
                      cashDesks={cashDesks}
                      incomeTags={incomeTags}
                      expenseTags={expenseTags}
                      validDebtFields={validDebtFields}
                      debtFieldsSchema={debtFieldsSchema}
                      onUpdate={updateCommand}
                      onRemove={removeCommand}
                      disabled={isSubmitting}
                      isHighlighted={highlightedCommandId === command.id}
                      onFieldKeyDown={handleFieldKeyDown}
                      fieldRefs={fieldRefs}
                    />
                  ))
                )}
              </>
            )}
          </Box>

          {/* Summary */}
          {commands.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                flexWrap: 'wrap',
                p: 1,
                backgroundColor: 'grey.100',
                borderRadius: 1,
              }}
            >
              <Chip
                label={`סה"כ: ${summary.total}`}
                size="small"
                variant="outlined"
              />
              {summary.pending > 0 && (
                <Chip
                  label={`ממתינים: ${summary.pending}`}
                  size="small"
                  color="default"
                />
              )}
              {summary.submitting > 0 && (
                <Chip
                  label={`שולח: ${summary.submitting}`}
                  size="small"
                  color="info"
                />
              )}
              {summary.success > 0 && (
                <Chip
                  label={`הצליחו: ${summary.success}`}
                  size="small"
                  color="success"
                />
              )}
              {summary.error > 0 && (
                <Chip
                  label={`נכשלו: ${summary.error}`}
                  size="small"
                  color="error"
                />
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'space-between', px: 2, py: 1.5 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {hasSuccessful && (
              <Button
                variant="outlined"
                color="inherit"
                size="small"
                startIcon={<ClearAllIcon />}
                onClick={clearSuccessful}
                disabled={isSubmitting}
                sx={{ direction: 'ltr' }}
              >
                נקה מוצלחים
              </Button>
            )}
            {commands.length > 0 && (
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<DeleteIcon />}
                onClick={clearAll}
                disabled={isSubmitting}
                sx={{ direction: 'ltr' }}
              >
                נקה הכל
              </Button>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {hasFailed && (
              <Button
                variant="outlined"
                color="warning"
                startIcon={<RefreshIcon />}
                onClick={handleRetry}
                disabled={isSubmitting}
                sx={{ direction: 'ltr' }}
              >
                נסה שוב ({summary.error})
              </Button>
            )}
            <Button
              variant="contained"
              color="primary"
              startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
              onClick={handleSubmit}
              disabled={!canSubmit}
              sx={{ direction: 'ltr' }}
            >
              {isSubmitting ? 'שולח...' : `שלח הכל (${summary.pending + summary.error})`}
            </Button>
            <Chip
              size="small"
              label="Ctrl+Enter"
              variant="outlined"
              sx={{ fontSize: '0.7rem' }}
            />
          </Box>
        </DialogActions>

        {/* Command Search Popup */}
        <CommandSearchPopup
          open={searchPopupOpen}
          anchorEl={searchPopupAnchor}
          onClose={closeSearchPopup}
          commandTypes={commandTypes}
          onSelectCommand={handleSelectCommand}
        />
      </MobileDialog>
    </LocalizationProvider>
  );
};

export default QuickCommandsDialog;
